/**
 * What a fabric balance is keyed by, and whether a sheet asks for more than the
 * store holds.
 *
 * Lifted out of FabricStore.jsx so it can be tested on its own. These two are
 * where a mistake is quietest: a key that disagrees with the server's puts a
 * line's stock warning against the wrong shelf, and demand that is not summed
 * per shelf waves through a sale the store cannot cover. Neither shows up as an
 * error — the form simply says the wrong thing.
 */

/**
 * The key a balance is held under: fabric, colour and print.
 *
 * A missing print collapses to 0 rather than staying null, mirroring the
 * `pattern_key` generated column the database keys its unique index on. Both
 * sides have to agree on what "no print" means, or a pattern-less line would
 * look up a shelf that the server thinks is a different one.
 */
export const shelfKey = (fabricTypeId, fabricColorId, fabricPatternId) =>
  `${fabricTypeId}:${fabricColorId}:${fabricPatternId ?? 0}`;

/**
 * The balances payload (`GET /fabricStock/balances`) as a Map, keyed for lookup.
 *
 * Absence is meaningful: a shelf with no row has never been stocked, and reads
 * as a zero balance — which is what the server would find too.
 */
export const balanceMap = (rows) => new Map(
  (rows || []).map((b) => [
    shelfKey(b.fabricTypeId, b.fabricColorId, b.fabricPatternId),
    { weight: Number(b.onHandWeight) || 0, rolls: Number(b.onHandRolls) || 0 },
  ]),
);

/**
 * Every print of one cloth in one colour, under one key.
 *
 * The order form asks a looser question than the store does. A shelf is a
 * print, but an order line's print is optional — and every shelf in the store
 * currently carries one, so a line that names no print would look up a
 * print-less shelf that does not exist and read as "not in stock" while the
 * cloth sits there in four prints. This is the rollup that answers it honestly.
 */
export const clothKey = (fabricTypeId, fabricColorId) => `${fabricTypeId}:${fabricColorId}`;

/** The balances payload rolled up per cloth+colour, prints summed. */
export const clothTotals = (rows) => {
  const totals = new Map();
  (rows || []).forEach((b) => {
    const key = clothKey(b.fabricTypeId, b.fabricColorId);
    const at = totals.get(key) || { weight: 0, rolls: 0, prints: 0 };
    totals.set(key, {
      // Two decimals, the precision the balance is stored at: summing floats
      // otherwise prints a shelf of 48.8 + 120.55 as 169.35000000000002.
      weight: Math.round((at.weight + (Number(b.onHandWeight) || 0)) * 100) / 100,
      rolls: at.rolls + (Number(b.onHandRolls) || 0),
      prints: at.prints + 1,
    });
  });
  return totals;
};

/**
 * What the store holds for one order line, and how precisely it was asked.
 *
 * With a print named the answer is that one shelf; without, it is the cloth in
 * that colour across every print it comes in — `exact: false`, so the caller can
 * say "across 3 prints" rather than implying the whole figure is reachable for
 * a line that has not chosen one yet.
 *
 * Null when the line is not yet a cloth: until a fabric and a colour are both
 * picked there is no balance to speak about, which is not the same as zero.
 */
export const onHandForLine = ({ byShelf, byCloth }, fabricTypeId, fabricColorId, fabricPatternId) => {
  if (!fabricTypeId || !fabricColorId) return null;

  if (fabricPatternId) {
    const held = byShelf.get(shelfKey(fabricTypeId, fabricColorId, fabricPatternId));
    return { weight: held?.weight || 0, rolls: held?.rolls || 0, prints: held ? 1 : 0, exact: true };
  }

  const held = byCloth.get(clothKey(fabricTypeId, fabricColorId));
  return { weight: held?.weight || 0, rolls: held?.rolls || 0, prints: held?.prints || 0, exact: false };
};

/**
 * The shelves a sheet asks more of than the store holds.
 *
 * Demand is summed per shelf before it is compared, because the service applies
 * lines one after another against a running balance: two lines of 8 kg against
 * 10 kg on hand fail on the second, so checking each line against the full
 * balance on its own would wave through a shelf the store cannot cover.
 *
 * A shelf with no balance row has never been stocked, and counts as short the
 * moment it is picked — there is nothing for it to draw on. That now includes a
 * print of a fabric that is otherwise well stocked: 200 kg of print 07 is no
 * help to a line asking for print 12.
 */
export const shortfalls = (lines, balances) => {
  const wanted = new Map();
  (lines || []).forEach((line) => {
    if (!line?.fabricTypeId || !line?.fabricColorId) return;
    const key = shelfKey(line.fabricTypeId, line.fabricColorId, line.fabricPatternId);
    const at = wanted.get(key) || { weight: 0, rolls: 0 };
    wanted.set(key, {
      weight: at.weight + (Number(line.weight) || 0),
      rolls: at.rolls + (Number(line.rolls) || 0),
    });
  });

  const short = new Map();
  wanted.forEach((want, key) => {
    const held = balances.get(key) || { weight: 0, rolls: 0 };
    const stocked = balances.has(key) && held.weight > 0;
    const shortWeight = Math.round((want.weight - held.weight) * 100) / 100;
    const shortRolls = want.rolls - held.rolls;
    if (!stocked || shortWeight > 0 || shortRolls > 0) {
      short.set(key, { ...held, stocked, shortWeight, shortRolls });
    }
  });
  return short;
};

/**
 * The shelves named by more than one line.
 *
 * A count line is a *total*, not an addition, so two lines for one shelf are
 * two contradictory answers to "what is on it" rather than two bundles to add
 * up. The server refuses the whole sheet; this runs first so the drawer can
 * name the shelf while it still has the names to hand, and so a sheet with the
 * mistake never leaves the browser.
 *
 * Mirrors StockTakeLines::repeatedShelves() on the server, through the same
 * shelfKey() that collapses a missing print to 0 — both sides have to agree on
 * what "no print" means or a pattern-less line would look distinct to one of
 * them and duplicated to the other.
 *
 * A line missing its fabric or colour is not yet a shelf, so it cannot yet be
 * a repeat of one; the required-field rules own that case.
 */
export const duplicateShelves = (lines) => {
  const seen = new Set();
  const repeated = [];

  (lines || []).forEach((line) => {
    if (!line?.fabricTypeId || !line?.fabricColorId) return;
    const key = shelfKey(line.fabricTypeId, line.fabricColorId, line.fabricPatternId);
    if (seen.has(key) && !repeated.includes(key)) repeated.push(key);
    seen.add(key);
  });

  return repeated;
};

/**
 * What a counted line will actually write to the ledger for its shelf.
 *
 * The mirror of StockTakeLines::delta() on the server, and the reason the
 * drawer can promise what the count will do before it does it. A count is a
 * total, so the movement is the difference against what the books hold — a
 * shelf holding 610 kg and counted at 610 moves by nothing, and one counted at
 * 700 moves by 90, not by 700.
 *
 * A shelf with no balance row has never been stocked, so the whole counted
 * figure is the movement — the opening-balance case.
 *
 * Rounded to the two decimals the balance is stored at, so a shelf that agrees
 * reads as exactly zero rather than a float crumb the drawer would announce as
 * a change.
 */
export const countDelta = (line, held) => ({
  weight: Math.round(((Number(line?.weight) || 0) - (held?.weight || 0)) * 100) / 100,
  rolls: (Number(line?.rolls) || 0) - (held?.rolls || 0),
});

/** Below this many kilograms a shelf is running out, and the table says so. */
export const LOW_STOCK_KG = 200;

/**
 * Whether a shelf is low enough to flag in the table.
 *
 * Strictly below, so a shelf holding exactly 200 kg is not low. A threshold
 * that fires *on* its own number reads as an off-by-one to anyone checking the
 * colour against the figure printed beside it.
 *
 * An empty shelf is included: nothing is the lowest a shelf goes, and it is the
 * one most worth reordering. It only shows at all when the "in stock only"
 * toggle is off, which is the moment someone is looking for exactly that.
 */
export const isLowStock = (weight) => (Number(weight) || 0) < LOW_STOCK_KG;
